/**
 * ChequeSettingsFormDialog - Dialog for creating/editing cheque settings
 */

import type {
  ChequeSettingsPublic,
  ChequeStyle,
  CreateChequeSettingsRequest,
  UpdateChequeSettingsRequest,
} from '@garden-platform/shared';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useBankAccounts,
  useCreateChequeSettings,
  useSignatures,
  useUpdateChequeSettings,
} from '../../hooks';

interface FormData {
  name: string;
  bankAccountId: string;
  chequeStyle: ChequeStyle;
  startingChequeNumber: number;
  printSignature: boolean;
  signatureId: string;
  secondSignatureId: string;
  requireDualSignatureAbove: string;
  printCompanyLogo: boolean;
  printCompanyAddress: boolean;
  isActive: boolean;
}

const defaultFormData: FormData = {
  name: '',
  bankAccountId: '',
  chequeStyle: 'standard',
  startingChequeNumber: 1,
  printSignature: false,
  signatureId: '',
  secondSignatureId: '',
  requireDualSignatureAbove: '',
  printCompanyLogo: true,
  printCompanyAddress: true,
  isActive: true,
};

export interface ChequeSettingsFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chequeSettings?: ChequeSettingsPublic;
}

export const ChequeSettingsFormDialog: React.FC<ChequeSettingsFormDialogProps> = ({
  open,
  onOpenChange,
  chequeSettings,
}) => {
  const isEditing = !!chequeSettings;
  const createMutation = useCreateChequeSettings();
  const updateMutation = useUpdateChequeSettings();
  const { data: bankAccounts } = useBankAccounts(false);
  const { data: signatures } = useSignatures(false);

  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Reset form when dialog opens/closes or cheque settings changes
  useEffect(() => {
    if (open) {
      if (chequeSettings) {
        setFormData({
          name: chequeSettings.name,
          bankAccountId: chequeSettings.bankAccountId,
          chequeStyle: chequeSettings.chequeStyle,
          startingChequeNumber: chequeSettings.currentChequeNumber,
          printSignature: chequeSettings.printSignature,
          signatureId: chequeSettings.signatureId || '',
          secondSignatureId: chequeSettings.secondSignatureId || '',
          requireDualSignatureAbove: chequeSettings.requireDualSignatureAbove?.toString() || '',
          printCompanyLogo: chequeSettings.printCompanyLogo,
          printCompanyAddress: chequeSettings.printCompanyAddress,
          isActive: chequeSettings.isActive,
        });
      } else {
        setFormData(defaultFormData);
      }
      setErrors({});
    }
  }, [open, chequeSettings]);

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.bankAccountId) {
      newErrors.bankAccountId = 'Bank account is required';
    }

    if (formData.startingChequeNumber < 1) {
      newErrors.startingChequeNumber = 'Starting cheque number must be at least 1';
    }

    if (formData.printSignature && !formData.signatureId) {
      newErrors.signatureId = 'Primary signature is required when printing signatures';
    }

    if (formData.requireDualSignatureAbove && formData.secondSignatureId && !formData.signatureId) {
      newErrors.signatureId = 'Primary signature is required for dual signature';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const dualSignatureAmount = formData.requireDualSignatureAbove
      ? parseFloat(formData.requireDualSignatureAbove)
      : undefined;

    if (isEditing && chequeSettings) {
      const updateData: UpdateChequeSettingsRequest = {
        name: formData.name,
        bankAccountId: formData.bankAccountId,
        chequeStyle: formData.chequeStyle,
        printSignature: formData.printSignature,
        signatureId: formData.signatureId || null,
        secondSignatureId: formData.secondSignatureId || null,
        requireDualSignatureAbove: dualSignatureAmount ?? null,
        printCompanyLogo: formData.printCompanyLogo,
        printCompanyAddress: formData.printCompanyAddress,
        isActive: formData.isActive,
      };

      await updateMutation.mutateAsync({ id: chequeSettings.id, data: updateData });
    } else {
      const createData: CreateChequeSettingsRequest = {
        name: formData.name,
        bankAccountId: formData.bankAccountId,
        chequeStyle: formData.chequeStyle,
        startingChequeNumber: formData.startingChequeNumber,
        printSignature: formData.printSignature,
        signatureId: formData.signatureId || undefined,
        secondSignatureId: formData.secondSignatureId || undefined,
        requireDualSignatureAbove: dualSignatureAmount,
        printCompanyLogo: formData.printCompanyLogo,
        printCompanyAddress: formData.printCompanyAddress,
      };

      await createMutation.mutateAsync(createData);
    }

    onOpenChange(false);
  };

  const getInputClassName = (field: keyof FormData) =>
    cn(errors[field] && 'border-red-500 focus:border-red-500 focus:ring-red-500');

  const isPending = createMutation.isPending || updateMutation.isPending;

  const activeBankAccounts = bankAccounts?.filter((ba) => ba.isActive) || [];
  const activeSignatures = signatures?.filter((s) => s.isActive) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Cheque Settings' : 'Add Cheque Settings'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the cheque printing configuration.'
              : 'Configure cheque printing settings for a bank account.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="name">
              Configuration Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Main Cheque Account"
              className={getInputClassName('name')}
            />
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Bank Account */}
          <div className="space-y-1.5">
            <Label htmlFor="bankAccountId">
              Bank Account <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.bankAccountId}
              onValueChange={(v) => handleChange('bankAccountId', v)}
            >
              <SelectTrigger id="bankAccountId" className={getInputClassName('bankAccountId')}>
                <SelectValue placeholder="Select a bank account" />
              </SelectTrigger>
              <SelectContent>
                {activeBankAccounts.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No bank accounts available
                  </div>
                ) : (
                  activeBankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.bankName}
                      {account.accountName && ` (${account.accountName})`}
                      {' - ****'}
                      {account.accountNumberLastFour}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {errors.bankAccountId && <p className="text-sm text-red-600">{errors.bankAccountId}</p>}
          </div>

          {/* Cheque Style */}
          <div className="space-y-1.5">
            <Label htmlFor="chequeStyle">Cheque Style</Label>
            <Select
              value={formData.chequeStyle}
              onValueChange={(v) => handleChange('chequeStyle', v as ChequeStyle)}
            >
              <SelectTrigger id="chequeStyle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="voucher">Voucher</SelectItem>
                <SelectItem value="wallet">Wallet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Starting Cheque Number (only for new settings) */}
          {!isEditing && (
            <div className="space-y-1.5">
              <Label htmlFor="startingChequeNumber">Starting Cheque Number</Label>
              <Input
                id="startingChequeNumber"
                type="number"
                min={1}
                value={formData.startingChequeNumber}
                onChange={(e) =>
                  handleChange('startingChequeNumber', parseInt(e.target.value, 10) || 1)
                }
                className={getInputClassName('startingChequeNumber')}
              />
              {errors.startingChequeNumber && (
                <p className="text-sm text-red-600">{errors.startingChequeNumber}</p>
              )}
            </div>
          )}

          {/* Print Options */}
          <div className="space-y-3">
            <Label>Print Options</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="printCompanyLogo"
                  checked={formData.printCompanyLogo}
                  onCheckedChange={(checked) => handleChange('printCompanyLogo', checked === true)}
                />
                <Label htmlFor="printCompanyLogo" className="font-normal">
                  Print company logo on cheques
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="printCompanyAddress"
                  checked={formData.printCompanyAddress}
                  onCheckedChange={(checked) =>
                    handleChange('printCompanyAddress', checked === true)
                  }
                />
                <Label htmlFor="printCompanyAddress" className="font-normal">
                  Print company address on cheques
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="printSignature"
                  checked={formData.printSignature}
                  onCheckedChange={(checked) => handleChange('printSignature', checked === true)}
                />
                <Label htmlFor="printSignature" className="font-normal">
                  Print signature on cheques
                </Label>
              </div>
            </div>
          </div>

          {/* Signature Selection */}
          {formData.printSignature && (
            <div className="space-y-4">
              {/* Primary Signature */}
              <div className="space-y-1.5">
                <Label htmlFor="signatureId">
                  Primary Signature <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.signatureId}
                  onValueChange={(v) => handleChange('signatureId', v)}
                >
                  <SelectTrigger id="signatureId" className={getInputClassName('signatureId')}>
                    <SelectValue placeholder="Select a signature" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeSignatures.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No signatures available
                      </div>
                    ) : (
                      activeSignatures.map((signature) => (
                        <SelectItem key={signature.id} value={signature.id}>
                          {signature.name}
                          {signature.title && ` - ${signature.title}`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.signatureId && <p className="text-sm text-red-600">{errors.signatureId}</p>}
              </div>

              {/* Secondary Signature */}
              <div className="space-y-1.5">
                <Label htmlFor="secondSignatureId">Secondary Signature (optional)</Label>
                <Select
                  value={formData.secondSignatureId || 'none'}
                  onValueChange={(v) => handleChange('secondSignatureId', v === 'none' ? '' : v)}
                >
                  <SelectTrigger id="secondSignatureId">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {activeSignatures
                      .filter((s) => s.id !== formData.signatureId)
                      .map((signature) => (
                        <SelectItem key={signature.id} value={signature.id}>
                          {signature.name}
                          {signature.title && ` - ${signature.title}`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dual Signature Amount */}
              {formData.secondSignatureId && (
                <div className="space-y-1.5">
                  <Label htmlFor="requireDualSignatureAbove">
                    Require Dual Signature Above Amount (optional)
                  </Label>
                  <Input
                    id="requireDualSignatureAbove"
                    type="number"
                    min={0}
                    step="0.01"
                    value={formData.requireDualSignatureAbove}
                    onChange={(e) => handleChange('requireDualSignatureAbove', e.target.value)}
                    placeholder="e.g., 5000"
                  />
                  <p className="text-xs text-muted-foreground">
                    Cheques above this amount will require both signatures
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Active Status (only for editing) */}
          {isEditing && (
            <div className="space-y-1.5">
              <Label htmlFor="isActive">Status</Label>
              <Select
                value={formData.isActive ? 'active' : 'inactive'}
                onValueChange={(v) => handleChange('isActive', v === 'active')}
              >
                <SelectTrigger id="isActive">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Settings'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

ChequeSettingsFormDialog.displayName = 'ChequeSettingsFormDialog';
